import { useEffect, useMemo, useRef } from 'react';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import type { LocationPaymentEvent, TerminalStateEvent } from '@ordr-as/payment-service-types/epos';
import config from '../config';
import { useAuth } from '../context/AuthContext';
import { useTerminalStore } from '../context/TerminalStore';

function isPaymentEvent(event: unknown): event is LocationPaymentEvent {
  return typeof event === 'object' && event !== null && 'paymentReference' in event && 'state' in event;
}

const RETRY_INTERVAL_MS = 3000;

export function useTerminalEventStream(locationId: number, terminalIds: string[]) {
  const { getAccessToken } = useAuth();
  const actions = useTerminalStore((s) => s.actions);
  const terminalIdsKey = terminalIds.join(',');

  // Keep getAccessToken in a ref so the fetch wrapper always uses the latest
  const getTokenRef = useRef(getAccessToken);
  getTokenRef.current = getAccessToken;

  const url = useMemo(() => {
    if (!terminalIdsKey) {
      return null;
    }
    const params = new URLSearchParams();
    for (const id of terminalIdsKey.split(',')) {
      params.append('ids', id);
    }
    return `${config.paymentServiceUrl}/v1/client/locations/${locationId}/terminals/events?${params}`;
  }, [locationId, terminalIdsKey]);

  useEffect(() => {
    if (!url) {
      actions.setSseState('disconnected');
      return;
    }

    const controller = new AbortController();

    // Custom fetch wrapper that gets a fresh token on every attempt (initial + retries)
    const authenticatedFetch: typeof fetch = async (input, init) => {
      const token = await getTokenRef.current();
      const headers = new Headers(init?.headers);
      headers.set('Authorization', `Bearer ${token}`);
      return fetch(input, { ...init, headers });
    };

    actions.setSseState('connecting');

    fetchEventSource(url, {
      fetch: authenticatedFetch,
      headers: { Accept: 'text/event-stream' },
      onclose() {
        // Server closed the stream gracefully (deploy, LB timeout, etc).
        // Throw to enter onerror path which triggers a retry.
        throw new Error('SSE stream closed by server');
      },
      onerror() {
        actions.setSseState('disconnected');
        // Return retry interval — library will call create() again with fresh auth via our fetch wrapper
        return RETRY_INTERVAL_MS;
      },
      onmessage(msg) {
        if (!msg.data) return;
        try {
          const event = JSON.parse(msg.data);
          if (config.env.includes('local')) console.log('[SSE]', event); // oxlint-disable-line no-console -- local dev only
          if (event.replay) return; // polling handles initial state
          if (isPaymentEvent(event)) {
            actions.handlePaymentEvent(event as LocationPaymentEvent);
          } else if ('pspTerminalId' in event) {
            actions.handleTerminalEvent(event as TerminalStateEvent);
          }
        } catch {
          // skip malformed events
        }
      },
      async onopen(response) {
        if (response.ok) {
          actions.setSseState('connected');
          return;
        }
        throw new Error(`SSE response ${response.status}`);
      },
      openWhenHidden: true,
      signal: controller.signal,
    });

    return () => {
      controller.abort();
      actions.setSseState('disconnected');
    };
  }, [url, actions]);
}
